import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { validatePhysicianField } from "./scriptcophysicianopportunity.helper";

const getProviderName = (provider, opportunity) => {
  if (provider.firstName) {
    return `${provider.firstName} ${provider.lastName || ""}`.trim();
  }
  return provider.name || opportunity?.physicianName || "";
};
const getFieldSx = (disabled, hasError) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F9FAFB" : "#FFFFFF",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: hasError ? "#d32f2f" : "#E8E8E9",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    py: 1.5,
    color: disabled ? "#66638C" : "#0e1324",
    WebkitTextFillColor: disabled ? "#66638C" : "#0e1324",
    pr: 0,
  },
});

const ScriptCoPhysicianOpportunity = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
  disabled = false,
}) => {
  const provider = opportunity?.providerDetails || opportunity?.physician || {};
  const initialName =
    data.name || data.physicianName || getProviderName(provider, opportunity);
  const initialNpi = data.npi || provider.npi || opportunity?.npi || "";
  const initialPhone =
    data.phone ||
    provider.phone ||
    provider.phoneNumber ||
    opportunity?.phone ||
    "";
  const initialFax =
    data.fax || provider.fax || provider.faxNumber || opportunity?.fax || "";

  const [formData, setFormData] = useState(() => ({
    name: initialName,
    npi: initialNpi,
    phone: initialPhone,
    fax: initialFax,
  }));

  // Sync initial data back to parent if it was empty
  useEffect(() => {
    if (!data.name && !data.physicianName && onDataChange) {
      onDataChange(formData);
    }
  }, [data.name, data.physicianName, formData, onDataChange]);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const fields = ["name", "npi", "phone", "fax"];
    const initialErrors = {};
    fields.forEach((field) => {
      const error = validatePhysicianField(field, formData[field]);
      if (error) initialErrors[field] = error;
    });
    setErrors(initialErrors);
  }, [formData]);

  const validateField = (name, value) => {
    const error = validatePhysicianField(name, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    if (disabled) return;
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      onDataChange(next);
      return next;
    });
    validateField(name, value);
  };

  const handleBlur = (e) => {
    if (disabled) return;
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: !disabled && isInvalid && shouldShow,
      helperText: !disabled && shouldShow ? errors[name] : "",
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        Prescribing Physician Details
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Physician Name
            </Typography>
            <TextField
              name="name"
              placeholder="Enter physician name"
              variant="outlined"
              size="small"
              disabled={disabled}
              value={formData.name || ""}
              onChange={(e) => {
                if (!disabled && e.target.value.length <= 120) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("name")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.name || "").length}/120
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={getFieldSx(disabled, getErrorProps("name").error)}
            />
          </Box>
        </Box>

        {/* NPI */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              NPI
            </Typography>
            <TextField
              name="npi"
              placeholder="Enter NPI"
              variant="outlined"
              size="small"
              disabled={disabled}
              value={formData.npi || ""}
              onChange={(e) => {
                if (!disabled && e.target.value.length <= 10) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("npi")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.npi || "").length}/10
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={getFieldSx(disabled, getErrorProps("npi").error)}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Phone */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Phone Number
            </Typography>
            <TextField
              name="phone"
              placeholder="Enter phone number"
              variant="outlined"
              size="small"
              disabled={disabled}
              value={formData.phone || ""}
              onChange={(e) => {
                if (!disabled && e.target.value.length <= 15) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("phone")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.phone || "").length}/15
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={getFieldSx(disabled, getErrorProps("phone").error)}
            />
          </Box>
        </Box>

        {/* Fax */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Fax Number
            </Typography>
            <TextField
              name="fax"
              placeholder="Enter fax number"
              variant="outlined"
              size="small"
              disabled={disabled}
              value={formData.fax || ""}
              onChange={(e) => {
                if (!disabled && e.target.value.length <= 15) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("fax")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.fax || "").length}/15
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={getFieldSx(disabled, getErrorProps("fax").error)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ScriptCoPhysicianOpportunity.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ScriptCoPhysicianOpportunity;
